import React, { useCallback, useState } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { X, AlertCircle, Settings } from 'lucide-react';
import { ProcessorNodeData } from '../../types/pipeline';
import { PROCESSOR_COLORS } from '../../types/processor';
import { usePipelineBuilderStore } from '../../store/pipelineBuilderStore';
import { useUIStore } from '../../store';
import { isNodeInExecutionFlow } from '../../utils/executionFlow';
import { QuickEditDialog } from './QuickEditDialog';

// Check if processor supports dynamic inputs
function supportsDynamicInputs(data: ProcessorNodeData): boolean {
  return (
    data.config?.supports_dynamic_inputs === true ||
    ['oneshot_document', 'agentic_task', 'orchestrator', 'vector', 'shell'].includes(data.processor_name)
  );
}

/**
 * Orchestrator Node Component (SWRM-121 Phase 3)
 * 
 * Special node type for orchestrator processors that:
 * - Has bottom handle for sub-agent connections
 * - Displays list of configured sub-agents
 * - Manages sub-agent chains through connections
 */
export const OrchestratorNode: React.FC<NodeProps<ProcessorNodeData>> = ({ id, data, selected }) => {
  const removeNode = usePipelineBuilderStore((state) => state.removeNode);
  const selectNode = usePipelineBuilderStore((state) => state.selectNode);
  const updateNode = usePipelineBuilderStore((state) => state.updateNode);
  const edges = usePipelineBuilderStore((state) => state.edges);
  const nodes = usePipelineBuilderStore((state) => state.nodes);
  const theme = useUIStore((state) => state.theme);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showQuickEdit, setShowQuickEdit] = useState(false);
  const [editingSubAgent, setEditingSubAgent] = useState<{name: string; display_name: string; description: string} | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  // Normalize processor_type to uppercase (backend returns lowercase)
  const processorType = (data.processor_type?.toUpperCase() || 'ORCHESTRATOR') as 'ORCHESTRATOR';
  const colors = PROCESSOR_COLORS[processorType];
  const hasErrors = data.validation_errors && data.validation_errors.length > 0;
  const hasWarnings = data.validation_warnings && data.validation_warnings.length > 0;
  
  // Check if node is connected to execution flow (for disabling data handles)
  const isInExecutionFlow = isNodeInExecutionFlow(id, edges, nodes);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    removeNode(id);
    setShowDeleteConfirm(false);
  }, [id, removeNode]);

  const handleCancelDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  }, []);

  const handleSettingsClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    selectNode(id);
  }, [id, selectNode]);

  const handleNodeClick = useCallback((e: React.MouseEvent) => {
    // Don't open quick edit if clicking on buttons or handles
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('.react-flow__handle')) {
      return;
    }
    e.stopPropagation(); // Prevent node selection
    setShowQuickEdit(true);
  }, []);

  const handleCloseQuickEdit = useCallback(async () => {
    setShowQuickEdit(false);
    
    // Trigger validation if pipeline was previously validated
    const state = usePipelineBuilderStore.getState();
    if (state.validatedAt) {
      try {
        const { buildPipelineSteps } = require('../../utils/executionFlow');
        const { apiService } = require('../../services/api');
        const steps = buildPipelineSteps(state.nodes, state.edges);
        const results = await apiService.validatePipeline({ steps });
        usePipelineBuilderStore.getState().setValidationResults(results);
      } catch (error) {
        console.error('Auto-revalidation after closing quick edit failed:', error);
      }
    }
  }, []);

  const handleEditSubAgent = useCallback((e: React.MouseEvent, subAgent: any) => {
    e.stopPropagation();
    setEditingSubAgent(subAgent);
    setEditDisplayName(subAgent.display_name);
    setEditDescription(subAgent.description);
  }, []);

  const handleCancelEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSubAgent(null);
    setEditDisplayName('');
    setEditDescription('');
  }, []);

  const handleSaveEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editingSubAgent) return;

    // Update the sub-agent in the config
    const currentSubAgents = (data.config?.sub_agents || []) as any[];
    const updatedSubAgents = currentSubAgents.map((sa: any) =>
      sa.name === editingSubAgent.name
        ? { ...sa, display_name: editDisplayName, description: editDescription }
        : sa
    );

    updateNode(id, {
      data: {
        ...data,
        config: {
          ...data.config,
          sub_agents: updatedSubAgents,
        },
      },
    });

    setEditingSubAgent(null);
    setEditDisplayName('');
    setEditDescription('');
  }, [editingSubAgent, editDisplayName, editDescription, data, id, updateNode]);

  // Get sub-agent config from node config
  const subAgents = (data.config?.sub_agents || []) as Array<{
    name: string;
    display_name: string;
    description: string;
    steps?: Array<{
      step_id: string;
      processor_name: string;
      validation_errors?: string[];
      validation_warnings?: string[];
    }>;
  }>;

  return (
    <>
      <div
        className={`
          ${colors.bg}
          border-2 
          ${hasErrors ? 'border-red-500 shadow-lg shadow-red-200' : 
            hasWarnings ? 'border-yellow-500 shadow-lg shadow-yellow-200' :
            selected ? 'border-blue-500 shadow-lg' : 
            `${colors.border} border-opacity-60`}
          rounded-lg w-[280px] shadow-md
          transition-all duration-200
          cursor-pointer
        `}
        onClick={handleNodeClick}
        data-testid="orchestrator-node"
      >
        {/* Header with execution flow handles */}
        <div className={`${colors.badge} px-3 py-2 rounded-t-md flex items-center justify-between relative`}>
          {/* Execution flow handle (previous_node) - left side, square shape */}
          <Handle
            type="target"
            position={Position.Left}
            id="previous_node"
            className="execution-handle !w-3 !h-3 !border-2 !border-slate-500 dark:!border-gray-300"
            style={{
              background: theme === 'dark' ? '#e5e5e5' : '#64748b',
              borderRadius: '2px',
              left: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
            }}
            data-testid="previous-node-handle"
          />
          
          <button
            onClick={handleSettingsClick}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Open detailed settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          
          <div className="flex items-center gap-2 flex-1">
            <span className="font-semibold text-sm truncate">
              {data.name || 'Orchestrator'}
            </span>
            {hasErrors && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                <AlertCircle className="w-3 h-3" />
                {data.validation_errors!.length}
              </span>
            )}
            {!hasErrors && hasWarnings && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                <AlertCircle className="w-3 h-3" />
                {data.validation_warnings!.length}
              </span>
            )}
          </div>
          {!data.isLocked && (
            <button
              onClick={handleDeleteClick}
              className="p-1 hover:bg-red-500 hover:text-white rounded transition-colors ml-2"
              data-testid="delete-button"
              title="Delete node"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          
          {/* Execution flow handle (next_node) - right side, square shape */}
          <Handle
            type="source"
            position={Position.Right}
            id="next_node"
            className="execution-handle !w-3 !h-3 !border-2 !border-slate-500 dark:!border-gray-300"
            style={{
              background: theme === 'dark' ? '#e5e5e5' : '#64748b',
              borderRadius: '2px',
              right: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
            }}
            data-testid="next-node-handle"
          />
        </div>
        
        {/* Order badge - top-right corner */}
        {data.order !== undefined && (
          <div
            className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold z-20 shadow-md"
            data-testid="order-badge"
            title={`Execution order: ${data.order}`}
          >
            {data.order}
          </div>
        )}

        {/* Body */}
        <div className="bg-white dark:bg-gray-800 p-3 rounded-b-md space-y-3">
          {/* Step ID */}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            ID: {data.step_id}
          </div>

          {/* Inputs Section */}
          {(supportsDynamicInputs(data) || data.required_input_keys.length > 0 || data.optional_input_keys.length > 0) && (
            <div className="space-y-1">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                INPUTS:
              </div>
              
              {/* Required Inputs (always shown) */}
              {data.required_input_keys.map((key) => (
                <div
                  key={`input-req-${key}`}
                  className="flex items-center gap-2 text-xs"
                  title={!isInExecutionFlow ? 'Connect to execution flow first' : undefined}
                >
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={`input-${key}`}
                    className={`!w-3 !h-3 !border-2 relative ${
                      isInExecutionFlow 
                        ? '!bg-blue-500 !border-blue-600 !cursor-crosshair' 
                        : '!bg-gray-400 dark:!bg-gray-500 !border-gray-500 dark:!border-gray-600 !cursor-not-allowed opacity-50'
                    }`}
                    style={{ 
                      position: 'relative',
                      left: '-12px',
                      top: 'auto',
                      transform: 'none',
                      pointerEvents: isInExecutionFlow ? 'all' : 'none',
                    }}
                  />
                  <span className={`font-medium ${
                    isInExecutionFlow 
                      ? 'text-gray-700 dark:text-gray-300' 
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {key} <span className="text-red-500">*</span>
                    {!isInExecutionFlow && (
                      <span className="text-xs ml-1 text-gray-400">(connect flow first)</span>
                    )}
                  </span>
                </div>
              ))}
              
              {/* Optional Inputs (always shown) */}
              {data.optional_input_keys.map((key) => (
                <div
                  key={`input-opt-${key}`}
                  className="flex items-center gap-2 text-xs"
                  title={!isInExecutionFlow ? 'Connect to execution flow first' : undefined}
                >
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={`input-${key}`}
                    className={`!w-3 !h-3 !border-2 !border-dashed relative ${
                      isInExecutionFlow 
                        ? '!bg-gray-400 !border-gray-500 !cursor-crosshair' 
                        : '!bg-gray-400 dark:!bg-gray-500 !border-gray-500 dark:!border-gray-600 !cursor-not-allowed opacity-50'
                    }`}
                    style={{ 
                      position: 'relative',
                      left: '-12px',
                      top: 'auto',
                      transform: 'none',
                      pointerEvents: isInExecutionFlow ? 'all' : 'none',
                    }}
                  />
                  <span className={`${
                    isInExecutionFlow 
                      ? 'text-gray-500 dark:text-gray-400' 
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {key}
                    {!isInExecutionFlow && (
                      <span className="text-xs ml-1 text-gray-400">(connect flow first)</span>
                    )}
                  </span>
                </div>
              ))}

              {/* Dynamic Input Support: Show "prompt inputs" handle */}
              {supportsDynamicInputs(data) && (
                <div
                  className="flex items-center gap-2 text-xs"
                  title="Connect to add a custom input"
                >
                  <Handle
                    type="target"
                    position={Position.Left}
                    id="add-input"
                    className={`!w-3 !h-3 !border-2 relative ${
                      isInExecutionFlow
                        ? '!bg-purple-400 !border-purple-600 !cursor-crosshair'
                        : '!bg-gray-400 dark:!bg-gray-500 !border-gray-500 dark:!border-gray-600 !cursor-not-allowed opacity-50'
                    }`}
                    style={{
                      position: 'relative',
                      left: '-12px',
                      top: 'auto',
                      transform: 'none',
                      pointerEvents: isInExecutionFlow ? 'all' : 'none',
                      clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)', // Diamond shape
                    }}
                  />
                  <span className={`font-medium ${
                    isInExecutionFlow
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    ➕ prompt inputs
                    {!isInExecutionFlow && (
                      <span className="text-xs ml-1 text-gray-400">(connect flow first)</span>
                    )}
                  </span>
                </div>
              )}
              
              {/* Custom Inputs (for dynamic input processors) */}
              {data.custom_inputs?.map((customInput) => {
                // Get type information for this input from input_artifact_keys
                const inputConfig = data.input_artifact_keys?.[customInput.name];
                const inputType = inputConfig?.type || 'string';
                const {
                  getArtifactTypeColor,
                  getArtifactTypeLabel
                } = require('../../utils/artifactTypes');
                const typeColor = getArtifactTypeColor(inputType);
                const typeLabel = getArtifactTypeLabel(inputType);
                
                return (
                  <div
                    key={`input-custom-${customInput.name}`}
                    className="flex items-center gap-2 text-xs"
                    title={customInput.description || `Custom input: ${customInput.name}`}
                  >
                    <Handle
                      type="target"
                      position={Position.Left}
                      id={`input-${customInput.name}`}
                      className={`!w-3 !h-3 !border-2 relative ${
                        isInExecutionFlow
                          ? '!bg-blue-500 !border-blue-600 !cursor-crosshair'
                          : '!bg-gray-400 dark:!bg-gray-500 !border-gray-500 dark:!border-gray-600 !cursor-not-allowed opacity-50'
                      }`}
                      style={{
                        position: 'relative',
                        left: '-12px',
                        top: 'auto',
                        transform: 'none',
                        pointerEvents: isInExecutionFlow ? 'all' : 'none',
                      }}
                    />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`font-medium ${
                        isInExecutionFlow
                          ? 'text-gray-700 dark:text-gray-300'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}>
                        {customInput.name} <span className="text-red-500">*</span>
                        {!isInExecutionFlow && (
                          <span className="text-xs ml-1 text-gray-400">(connect flow first)</span>
                        )}
                      </span>
                      {/* Type badge - only show if connected */}
                      {inputConfig && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                          style={{
                            backgroundColor: `${typeColor}20`,
                            color: typeColor,
                            border: `1px solid ${typeColor}40`,
                          }}
                        >
                          {typeLabel}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Outputs Section */}
          {data.output_keys.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                OUTPUTS:
              </div>
              {data.output_keys.map((key) => {
                // Get type information for this output
                const outputType = data.output_types?.[key] || 'string';
                const {
                  getArtifactTypeColor,
                  getArtifactTypeLabel
                } = require('../../utils/artifactTypes');
                const typeColor = getArtifactTypeColor(outputType);
                const typeLabel = getArtifactTypeLabel(outputType);
                
                return (
                  <div
                    key={`output-${key}`}
                    className="flex items-center justify-between text-xs gap-2"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        {key}
                      </span>
                      {/* Type badge */}
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                        style={{
                          backgroundColor: `${typeColor}20`,
                          color: typeColor,
                          border: `1px solid ${typeColor}40`,
                        }}
                      >
                        {typeLabel}
                      </span>
                    </div>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={`output-${key}`}
                      className="!w-3 !h-3 !border-2 relative"
                      style={{ 
                        position: 'relative',
                        right: '-12px',
                        top: 'auto',
                        transform: 'none',
                        backgroundColor: typeColor,
                        borderColor: typeColor,
                      }}
                      data-output-type={outputType}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Dynamic Outputs Section (from prompts) */}
          {data.dynamic_outputs && data.dynamic_outputs.length > 0 && (
            <div className="space-y-1 mt-2">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                DYNAMIC OUTPUTS:
              </div>
              {data.dynamic_outputs.map((output) => {
                const {
                  getArtifactTypeColor,
                  getArtifactTypeLabel,
                  getDynamicOutputBadgeClass
                } = require('../../utils/artifactTypes');
                const { getDynamicOutputBorderColor } = require('../../utils/dynamicOutputs');
                const typeColor = getArtifactTypeColor(output.type);
                const typeLabel = getArtifactTypeLabel(output.type);
                const dynamicBadgeClass = getDynamicOutputBadgeClass();
                const dynamicBorderColor = getDynamicOutputBorderColor();
                
                return (
                  <div
                    key={`dynamic-output-${output.name}`}
                    className="flex items-center justify-between text-xs gap-2"
                  >
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        {output.name}
                      </span>
                      {/* Type badge */}
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                        style={{
                          backgroundColor: `${typeColor}20`,
                          color: typeColor,
                          border: `1px solid ${typeColor}40`,
                        }}
                      >
                        {typeLabel}
                      </span>
                      {/* Dynamic indicator */}
                      <span className={`text-[10px] font-semibold ${dynamicBadgeClass}`}>
                        ⚡ dynamic
                      </span>
                    </div>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={`output-${output.name}`}
                      className={`!w-3 !h-3 !border-2 relative ${dynamicBorderColor}`}
                      style={{
                        position: "relative",
                        right: "-12px",
                        top: "auto",
                        transform: "none",
                        backgroundColor: typeColor,
                      }}
                      data-output-type={output.type}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Sub-Agents List */}
          {subAgents.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                SUB-AGENTS: ({subAgents.length})
              </div>
              {subAgents.map((subAgent: any, index) => {
                // Aggregate validation errors/warnings from all steps in this sub-agent
                const subAgentSteps = subAgent.steps || [];
                const subAgentErrors = subAgentSteps.flatMap((step: any) => step.validation_errors || []);
                const subAgentWarnings = subAgentSteps.flatMap((step: any) => step.validation_warnings || []);
                const hasSubAgentErrors = subAgentErrors.length > 0;
                const hasSubAgentWarnings = subAgentWarnings.length > 0;

                return (
                  <div 
                    key={subAgent.name}
                    className={`text-xs rounded px-2 py-1 cursor-pointer transition-colors ${
                      hasSubAgentErrors
                        ? 'bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 border-2 border-red-500'
                        : hasSubAgentWarnings
                        ? 'bg-yellow-100 dark:bg-yellow-900 hover:bg-yellow-200 dark:hover:bg-yellow-800 border-2 border-yellow-500'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                    onClick={(e) => handleEditSubAgent(e, subAgent)}
                    title={
                      hasSubAgentErrors
                        ? `Validation errors: ${subAgentErrors.join('; ')}`
                        : hasSubAgentWarnings
                        ? `Validation warnings: ${subAgentWarnings.join('; ')}`
                        : 'Click to edit sub-agent name'
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-gray-800 dark:text-gray-200">
                        {subAgent.display_name}
                      </div>
                      {hasSubAgentErrors && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                          <AlertCircle className="w-3 h-3" />
                          {subAgentErrors.length}
                        </span>
                      )}
                      {!hasSubAgentErrors && hasSubAgentWarnings && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                          <AlertCircle className="w-3 h-3" />
                          {subAgentWarnings.length}
                        </span>
                      )}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400 truncate">
                      {subAgent.description}
                    </div>
                    {(hasSubAgentErrors || hasSubAgentWarnings) && (
                      <div className="mt-1 text-xs space-y-1">
                        {subAgentErrors.slice(0, 2).map((error: string, idx: number) => (
                          <div key={idx} className="text-red-700 dark:text-red-300 truncate">
                            • {error}
                          </div>
                        ))}
                        {subAgentWarnings.slice(0, 2).map((warning: string, idx: number) => (
                          <div key={idx} className="text-yellow-700 dark:text-yellow-300 truncate">
                            • {warning}
                          </div>
                        ))}
                        {(subAgentErrors.length + subAgentWarnings.length > 2) && (
                          <div className="text-gray-600 dark:text-gray-400 italic">
                            +{subAgentErrors.length + subAgentWarnings.length - 2} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* LLM Output Label - positioned near the handle */}
        <div 
          className="absolute text-xs text-purple-600 dark:text-purple-400 font-medium"
          style={{
            bottom: '-28px',
            left: '25%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          LLM Output
        </div>

        {/* Sub-Agents Label - positioned near the handle */}
        <div 
          className="absolute text-xs text-green-600 dark:text-green-400 font-medium"
          style={{
            bottom: '-28px',
            left: '75%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          Sub-Agents
        </div>

        {/* Bottom handle for LLM output */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="llm_output"
          className="!w-4 !h-4 !border-2 !border-purple-700 dark:!border-purple-500"
          style={{
            background: '#9333ea',
            bottom: '-10px',
            left: '25%',
            marginLeft: '-8px',
            zIndex: 10,
            borderRadius: '50%',
          }}
          data-testid="llm-output-handle"
          title="LLM Output - Connect to first processor inputs in sub-agents"
        />

        {/* Bottom handle for sub-agent connections - diamond shape */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="sub_agent_output"
          className="!w-4 !h-4 !border-2 !border-green-600 dark:!border-green-400"
          style={{
            background: '#22c55e',
            bottom: '-10px',
            left: '75%',
            marginLeft: '-8px',
            zIndex: 10,
            borderRadius: '0',
            transform: 'rotate(45deg)',
          }}
          data-testid="sub-agent-handle"
          title="Connect sub-agent processors here"
        />
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div 
          className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 rounded-lg"
          onClick={handleCancelDelete}
        >
          <div 
            className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-xl max-w-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-sm font-semibold mb-2">Delete Node?</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-4">
              This will remove the orchestrator and all its sub-agents. This cannot be undone.
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit sub-agent modal */}
      {editingSubAgent && (
        <div 
          className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 rounded-lg"
          onClick={handleCancelEdit}
        >
          <div 
            className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-xl max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-sm font-semibold mb-3">Edit Sub-Agent</div>
            
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                  Name (internal ID)
                </label>
                <input
                  type="text"
                  value={editingSubAgent.name}
                  disabled
                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
              </div>
              
              <div>
                <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter display name"
                />
              </div>
              
              <div>
                <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter description"
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Edit Dialog */}
      {showQuickEdit && (
        <QuickEditDialog nodeId={id} onClose={handleCloseQuickEdit} />
      )}
    </>
  );
};

